import React from 'react';
import {
    canIncludeDiarization,
    diarizationStateBlock,
    AUDIO_REQUIRED_KIND,
} from './useActivityDiarization';
import { INSUFFICIENT_POINTS_MESSAGE } from '../../Matching/aiQuota.js';
import { HAND_EDIT_REPLACED_NOTICE } from '../../../services/diarizationApi.js';

/** 含めないとき（残量が足りない）に §5.2 No.1 の文言に続けて出す文言 (SCR-D-001 §6.2「確認モーダルの表示」) */
export const NOT_INCLUDED_MESSAGE = '話者分離は実行されません。文字起こし・要約・フィードバックは実行します。';

/**
 * 含めるときの文言 (SCR-D-001 §6.2「確認モーダルの表示」: 話者分離も行うこと・結果は数分〜最大 24 時間後に出ること・
 * 必要な AI ポイントの数を添える)。
 * 仮定: 文言は下のとおりとした（SCR-D-001 は添える内容だけを定め、文言を定めていない。未確認）。
 * 必要なポイントが分からない（面談の音声の応答の `required_points` が null 等）ときは、数の部分を出さない
 *
 * @param {number|null} requiredPoints
 * @returns {string[]}
 */
export function includedMessages(requiredPoints) {
    const points = typeof requiredPoints === 'number' ? `（AI ポイントを ${requiredPoints} 使います）` : '';
    return [
        `話者分離も行います${points}。`,
        '話者分離の結果は、数分から最大 24 時間後に出ます。',
    ];
}

/**
 * 確認モーダル「AI処理の確認」に添える話者分離の文言 (SCR-D-001 §6.2「確認モーダルの表示」)。
 *
 * | 場合 | 出すもの |
 * | 含める（§5.2.1 の判定を満たす） | 話者分離も行うこと・結果の出る時期・必要なポイント |
 * | 含めない（残量が足りない） | §5.2 No.1 の文言 ＋ NOT_INCLUDED_MESSAGE |
 * | 含めない（フラグが無効・DIARIZATION のキーが無い＝音声の長さが上限を超えている場合を含む） | 何も出さない（既存の文言のまま） |
 * | 含めない（取り直した状態が、完了・再実行できない失敗のまま） | 何も出さない（既存の文言のまま。#1646） |
 *
 * **どの行が当たるかは上から順に見る** (§6.2): ① フラグが無効 ② 話者分離の状態が理由で含めない
 * ③ DIARIZATION のキーが無い ④ 残量が足りない ⑤ 含める。
 *
 * - ② と ③ は、どちらが当たっても表示は同じ（話者分離に触れない。#1646）。順は §6.2 のまま
 * - ② は ④ より先: 話者分離を行わない理由が残量ではないときに、残量不足の文を出さない
 *
 * 改める前（#1645〜#1646）は、完了の面談で「話者分離は完了しているため、やり直しません。…」の文を出していた。
 * 音声の確定でサーバが記録を消し、作り直せるようになったため、文を消した（§6.2 の表の下の改訂の注記）
 *
 * @param {{enabled: boolean, quota: Object|null, audioRequired?: ({kind: string, points: (number|null)}|null),
 *          diarization?: Object|null}} props
 *   audioRequired は面談の音声の応答から読んだ必要なポイント (readAudioRequiredPoints の返す形)。
 *   diarization は話者分離の状態で、音声の確定の後に取り直したもの (useActivityDiarization の diarizationForInclude)。
 *   渡さないとき（取り直しに失敗したときを含む）は状態を理由にしない
 */
function DiarizationConfirmNotice({ enabled, quota, audioRequired, diarization }) {
    if (!enabled) return null;

    if (diarizationStateBlock(diarization) !== null) return null;

    if (audioRequired?.kind === AUDIO_REQUIRED_KIND.keyMissing) return null;

    if (canIncludeDiarization(quota, audioRequired)) {
        return (
            <div className="diar-confirm-notice" data-testid="diar-confirm-included">
                {includedMessages(audioRequired?.points ?? null).map((text) => <p key={text}>{text}</p>)}
                {/* 話者分離の案内の最後に出す。含めないときは出さない
                    (§6.2「手で編集した内容が置き換わることの明示」①。#1755) */}
                <p data-testid="diar-confirm-hand-edit">{HAND_EDIT_REPLACED_NOTICE}</p>
            </div>
        );
    }
    return (
        <div className="diar-confirm-notice" data-testid="diar-confirm-not-included">
            <p>{INSUFFICIENT_POINTS_MESSAGE}</p>
            <p>{NOT_INCLUDED_MESSAGE}</p>
        </div>
    );
}

export default DiarizationConfirmNotice;
